// Portal settings slice. Holds the public runtime config (copy/branding +
// region info) the UI reads instead of hardcoding Alabama-specific text. Ships
// with the built-in defaults as initial data so the first paint is never blank
// while /api/public-config is in flight; the fetched payload overrides them.
import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { apiFetch } from '../api/public-client.js';
import { PORTAL_SETTINGS_DEFAULTS } from '../config/portal-settings-defaults.js';

export const fetchPublicConfig = createAsyncThunk(
  'settings/fetchPublicConfig',
  async (_arg, { rejectWithValue }) => {
    try {
      return await apiFetch('/api/public-config');
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

const initialState = {
  // settings: editable copy/branding (defaults until the fetch resolves).
  settings: PORTAL_SETTINGS_DEFAULTS,
  // logos: ordered partner/agency logos shown alongside the Sanborn vendor mark
  // (empty until the fetch resolves; an un-onboarded deployment has none).
  logos: [],
  showStateSelector: false,
  defaultStateFips: null,
  regionSubdivisionCount: null,
  loading: false,
  error: null,
};

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPublicConfig.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(fetchPublicConfig.fulfilled, (s, a) => {
        s.loading = false;
        // Merge over defaults so a partial payload never drops keys the UI needs.
        s.settings = { ...PORTAL_SETTINGS_DEFAULTS, ...(a.payload.settings || {}) };
        s.logos = Array.isArray(a.payload.logos) ? a.payload.logos : [];
        s.showStateSelector = Boolean(a.payload.showStateSelector);
        s.defaultStateFips = a.payload.defaultStateFips ?? null;
        s.regionSubdivisionCount = a.payload.regionSubdivisionCount ?? null;
      })
      .addCase(fetchPublicConfig.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload || 'Failed to load portal configuration';
      });
  },
});

export default settingsSlice.reducer;

// Selector helpers so components don't reach into shape details.
export const selectSettings = (state) => state.settings.settings;

// The ordered partner/agency logos for the co-branding lockup.
export const selectLogos = (state) => state.settings.logos;

// Partner logos only — excludes any vendor-role row so it never doubles up with
// the inline Sanborn mark rendered alongside it. Memoized so the filtered array
// keeps a stable reference across unrelated store updates.
export const selectPartnerLogos = createSelector(
  selectLogos,
  (logos) => logos.filter((logo) => logo.role !== 'vendor'),
);
